import React from "react";
import { RefreshCw, Image as ImageIcon } from "lucide-react";
import { Button, Field, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";

interface SmartLinkPreviewProps {
  ogImageUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  isFetchingMetadata: boolean;
  onRefresh: () => void;
  destinationUrl: string;
}

export const SmartLinkPreview: React.FC<SmartLinkPreviewProps> = ({
  ogImageUrl,
  ogTitle,
  ogDescription,
  isFetchingMetadata,
  onRefresh,
  destinationUrl,
}) => {
  return (
    <Field className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Field.Label>Link Preview</Field.Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
          onClick={onRefresh}
          disabled={isFetchingMetadata || !destinationUrl}
        >
          <RefreshCw className={cn("size-3 mr-1", isFetchingMetadata && "animate-spin")} />
          Refresh Metadata
        </Button>
      </div>
      <div className="border rounded-xl overflow-hidden bg-slate-100 flex flex-col shadow-sm">
        <Skeleton loading={isFetchingMetadata} className="aspect-video w-full">
          {ogImageUrl ? (
            <div className="relative w-full aspect-video overflow-hidden bg-slate-200">
              <img
                src={ogImageUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover blur-xl opacity-30 scale-110"
              />
              <img src={ogImageUrl} alt="Preview" className="relative z-10 w-full h-full object-contain" />
            </div>
          ) : (
            <div className="w-full aspect-video flex items-center justify-center bg-slate-200 text-slate-400">
              <ImageIcon className="size-10" />
            </div>
          )}
        </Skeleton>
        <div className="p-4 flex flex-col gap-2">
          <Skeleton loading={isFetchingMetadata} className="h-6 w-3/4">
            <h4 className="font-bold text-foreground line-clamp-1">{ogTitle || "No Title"}</h4>
          </Skeleton>
          <Skeleton loading={isFetchingMetadata} className="h-12 w-full">
            <p className="text-sm text-muted-foreground line-clamp-2">
              {ogDescription || "No description available for this link."}
            </p>
          </Skeleton>
        </div>
      </div>
      <p className="text-xs text-muted-foreground italic px-1">
        This preview is what users will see when this link is added to their Player Card.
      </p>
    </Field>
  );
};
